/**
 * Pick how much. A named serving × quantity, or plain grams — and the numbers
 * update as you type, so "is 1.5 cups worth it" is answered before logging.
 */

import { useMemo, useState } from 'react'

import type { FoodItem } from '../lib/foodItem'
import { fmt, fmtKcal, scale } from '../lib/nutrients'
import { defaultQty, gramsFor, servingOptions } from '../lib/servings'
import { sourceLabel } from '../lib/foodItem'

export type Portion = { grams: number; serving_label: string | null; serving_qty: number | null }

export function FoodPortion({ item, actionLabel, onSubmit, busy }: {
  item: FoodItem
  actionLabel: string
  onSubmit: (p: Portion) => void
  busy?: boolean
}) {
  const opts = useMemo(() => servingOptions(item), [item])
  const initial = (() => {
    if (item.lastServing) {
      const i = opts.findIndex((o) => o.label === item.lastServing)
      if (i >= 0) return { i, q: item.lastQty ?? 1 }
    }
    if (item.lastGrams) return { i: opts.length - 1, q: item.lastGrams }
    return { i: 0, q: defaultQty(opts[0]) }
  })()
  const [idx, setIdx] = useState(initial.i)
  const [qtyText, setQtyText] = useState(String(initial.q))
  const opt = opts[idx]
  const qty = Number(qtyText.replace(',', '.'))
  const grams = Number.isFinite(qty) && qty > 0 ? gramsFor(opt, qty) : 0
  const n = scale(item.per100, grams)

  return (
    <div>
      <div style={{ marginTop: '.5rem' }}>
        <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{item.name}</div>
        <div className="muted small">
          {[item.brand, sourceLabel(item.source), item.verified ? 'verified' : null].filter(Boolean).join(' · ')}
        </div>
      </div>

      <div className="grid2">
        <div>
          <label htmlFor="qty">Amount</label>
          <input id="qty" inputMode="decimal" value={qtyText} onChange={(e) => setQtyText(e.target.value)}
            onFocus={(e) => e.target.select()} />
        </div>
        <div>
          <label htmlFor="unit">Unit</label>
          <select id="unit" value={idx} onChange={(e) => {
            const i = Number(e.target.value)
            setIdx(i)
            setQtyText(String(defaultQty(opts[i])))
          }}>
            {opts.map((o, i) => <option key={o.label + i} value={i}>{o.unit ? o.label : `${o.label}`}</option>)}
          </select>
        </div>
      </div>
      {!opt.unit && grams > 0 && <p className="muted small" style={{ margin: '.3rem 0 0' }}>= {fmt(grams)} {item.basis}</p>}

      <div className="card" style={{ marginTop: '1rem', padding: '.9rem 1rem' }}>
        <div className="row">
          <span className="big num">{fmtKcal(n.kcal ?? 0)}</span>
          <span className="muted">kcal</span>
          <span style={{ flex: 1 }} />
        </div>
        <div className="grid3 small" style={{ marginTop: '.5rem' }}>
          <span><b style={{ color: 'var(--protein)' }}>P</b> {fmt(n.protein_g ?? 0)} g</span>
          <span><b style={{ color: 'var(--carbs)' }}>C</b> {fmt(n.carbs_g ?? 0)} g</span>
          <span><b style={{ color: 'var(--fat)' }}>F</b> {fmt(n.fat_g ?? 0)} g</span>
        </div>
        {(n.fibre_g !== undefined || n.sodium_mg !== undefined || n.sugars_g !== undefined) && (
          <div className="muted small" style={{ marginTop: '.35rem' }}>
            {[n.fibre_g !== undefined && `Fibre ${fmt(n.fibre_g)} g`,
              n.sugars_g !== undefined && `Sugars ${fmt(n.sugars_g)} g`,
              n.sodium_mg !== undefined && `Sodium ${fmt(n.sodium_mg)} mg`].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>

      <button style={{ width: '100%' }} disabled={busy || grams <= 0}
        onClick={() => onSubmit({ grams, serving_label: opt.unit ? null : opt.label, serving_qty: opt.unit ? null : qty })}>
        {actionLabel}
      </button>
    </div>
  )
}
